import { type SVGProps } from 'react';

import { Box, type BoxProps, Flex, Text } from '@chakra-ui/react';

type RateBucketProps = BoxProps & {
  title: string;
  percent: number;
  index: number;
};

export const RateBucket: React.FC<RateBucketProps> = (props) => {
  return (
    <Box position="relative" {...props}>
      <Flex
        position="absolute"
        alignItems="center"
        direction="column"
        transform="translate(-50%,-50%)"
        zIndex={10}
        left="50%"
        top="50%"
      >
        <Text
          width="9.125rem"
          letterSpacing="-0.32px"
          textAlign="center"
          lineHeight="24px"
          fontWeight="400"
        >
          {props.title}
        </Text>
        <Text
          width="9.125rem"
          letterSpacing="-0.64px"
          fontWeight="700"
          lineHeight="44px"
          textAlign="center"
          fontSize="2rem"
          marginTop="0.75rem"
        >
          {props.percent.toPrecision(2)}
        </Text>
      </Flex>
      <Box position="relative">
        <RateBucketSVG percent={props.percent} index={props.index} />
      </Box>
    </Box>
  );
};

interface CustomSVGProps extends SVGProps<SVGSVGElement> {
  percent: number;
  index: number;
}

export const RateBucketSVG = ({ percent, index, ...props }: CustomSVGProps) => {
  const filterIdB = `filterB-${index}`;
  const filterIdD = `filterD-${index}`;
  const maskId = `mask-${index}`;
  const linearGradientId = `gradient-${index}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="11.125rem"
      height="11.125rem"
      fill="none"
      viewBox="0 0 186 186"
      {...props}
    >
      <mask
        id={maskId}
        width={185}
        height={185}
        x={12}
        y={12}
        maskUnits="userSpaceOnUse"
        style={{
          maskType: 'alpha',
        }}
      >
        <path
          fill="#CFBAE9"
          stroke="#834DC7"
          strokeWidth={0.8}
          d="M173.6 93C173.6 137.514 137.514 173.6 93 173.6C48.4859 173.6 12.4 137.514 12.4 93C12.4 81.8099 12.4006 71.1829 13.6623 61.5229C14.9234 51.8666 17.4412 43.2089 22.4541 35.9517C32.4636 21.4608 52.5378 12.4 93 12.4C133.462 12.4 153.536 21.4608 163.546 35.9517C168.559 43.2089 171.077 51.8666 172.338 61.5229C173.599 71.1829 173.6 81.8099 173.6 93Z"
        />
      </mask>
      <g mask={`url(#${maskId})`}>
        <g filter={`url(#${filterIdB})`}>
          <path
            fill="#fff"
            d="M174 93C174 137.735 137.735 174 93 174C48.2649 174 12 137.735 12 93C12 48.2649 12 12 93 12C174 12 174 48.2649 174 93Z"
          />
        </g>
        <path fill={`url(#${linearGradientId})`} d="M12 0H174V186H12z" />
        <g filter={`url(#${filterIdD})`}>
          <path
            stroke="#45AD7D"
            strokeWidth={0.8}
            d="M173.6 93C173.6 137.514 137.514 173.6 93 173.6C48.4859 173.6 12.4 137.514 12.4 93C12.4 81.8099 12.4006 71.1829 13.6623 61.5229C14.9234 51.8666 17.4412 43.2089 22.4541 35.9517C32.4636 21.4608 52.5378 12.4 93 12.4C133.462 12.4 153.536 21.4608 163.546 35.9517C168.559 43.2089 171.077 51.8666 172.338 61.5229C173.599 71.1829 173.6 81.8099 173.6 93Z"
            shapeRendering="crispEdges"
          />
        </g>
      </g>
      <defs>
        <filter
          id={filterIdB}
          width={186}
          height={186}
          x={0}
          y={0}
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation={6} />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.16 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
        <filter
          id={filterIdD}
          width={186}
          height={186}
          x={0}
          y={0}
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation={6} />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.16 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
        </filter>
        <linearGradient
          id={linearGradientId}
          x1={185}
          x2={185}
          y1={
            184 -
            scaleValue({
              value: percent,
              oldMin: 0,
              oldMax: 100,
              newMin: 0,
              newMax: 184,
            })
          }
          y2={185}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset={0} stopColor="#4FFFAA" stopOpacity={0} />
          <stop offset={0.12} stopColor="#4FFFAA" />
          <stop offset={1} stopColor="#63AEBB" />
        </linearGradient>
      </defs>
    </svg>
  );
};

type ScaleValueParams = {
  value: number;
  oldMin: number;
  oldMax: number;
  newMin: number;
  newMax: number;
};

function scaleValue({
  value,
  oldMin,
  oldMax,
  newMin,
  newMax,
}: ScaleValueParams) {
  return ((value - oldMin) / (oldMax - oldMin)) * (newMax - newMin) + newMin;
}
